import type { StaticImageData } from 'next/image'

import { cn } from '@/utilities/ui'
import type { LexicalRichTextBody } from '@/components/RichText/blockConvertersTypes'
import React from 'react'

import { MEDIA_FIGURE_SHELL_CLASS, MediaFigure } from '@/components/Media/MediaFigure'

import type { Media as MediaType, MediaBlock as MediaBlockProps } from '@/payload-types'

import {
  buildImageObjectFromMedia,
  ARTICLE_INLINE_MEDIA_SIZES,
  extractCaptionPlainText,
  getMediaIntrinsicDimensions,
} from '@/utilities/mediaImageObject'
import {
  buildContentFigcaptionText,
  buildVideoFigcaptionText,
  resolvePostContentImageAlt,
} from '@/utilities/postImageSeo'
import { getMediaUrl } from '@/utilities/getMediaUrl'

import { Media } from '../../components/Media'

type Props = MediaBlockProps & {
  breakout?: boolean
  captionClassName?: string
  className?: string
  enableGutter?: boolean
  imgClassName?: string
  staticImage?: StaticImageData
  disableInnerContainer?: boolean
  isFirstMediaInRichText?: boolean
  /** Título do post (fallback ALT / legenda quando aplicável). */
  postTitle?: string
  /** Texto do último H1–H3 antes deste bloco no Lexical (só artigos). */
  nearestHeading?: string | null
  /** Fallback ALT traduzido p/ imagem estática sem título/heading (namespace «Blocks»). */
  staticAltFallback?: string
  /**
   * `RichText` em Server Components; `RichTextForm` em árvores cliente (Form / pré-visualização).
   * Evita importar `server-only` no bundle do browser.
   */
  captionRichText: LexicalRichTextBody
}

/**
 * Bloco Payload: URL, dimensões, ALT com prioridade CMS + heading + título;
 * `<figure>` + `<figcaption>` sempre visíveis.
 */
export const MediaBlock: React.FC<Props> = (props) => {
  const {
    captionClassName,
    className,
    enableGutter = true,
    imgClassName,
    media,
    staticImage,
    disableInnerContainer,
    isFirstMediaInRichText = false,
    postTitle,
    nearestHeading,
    staticAltFallback,
    captionRichText: CaptionRichText,
  } = props

  const caption =
    media && typeof media === 'object' ? (media as MediaType).caption : undefined
  const hasCaption = Boolean(caption?.root?.children?.length)

  const imageLd =
    media && typeof media === 'object' && !(media as MediaType).mimeType?.includes('video')
      ? buildImageObjectFromMedia(media as MediaType)
      : null

  const captionPlain = hasCaption ? extractCaptionPlainText(caption) : ''

  const priority = Boolean(isFirstMediaInRichText)
  const inlineSizes = priority ? undefined : ARTICLE_INLINE_MEDIA_SIZES

  const seoFigureAttributes = imageLd
    ? {
        'data-image-object-url': imageLd.url,
        'data-image-object-width': String(imageLd.width ?? ''),
        'data-image-object-height': String(imageLd.height ?? ''),
        ...(captionPlain ? { 'data-image-object-caption': captionPlain } : {}),
      }
    : undefined

  const figcaptionRichClasses = cn(
    '[&_.payload-richtext]:prose-sm [&_.payload-richtext]:text-muted-foreground [&_.payload-richtext]:italic',
    {
      container: !disableInnerContainer,
    },
    captionClassName,
  )

  const figcaptionPlainClasses = cn(
    'text-muted-foreground mt-2 max-w-none text-xs italic leading-relaxed sm:text-sm',
    {
      container: !disableInnerContainer,
    },
    captionClassName,
  )

  const shell = cn(
    {
      container: enableGutter,
    },
    className,
  )

  const richCaptionNode = hasCaption ? (
    <CaptionRichText data={caption!} enableGutter={false} enableProse />
  ) : null

  // ── Vídeo
  if (media && typeof media === 'object' && (media as MediaType).mimeType?.includes('video')) {
    const videoFigcaption = buildVideoFigcaptionText({
      captionPlainFromCms: captionPlain,
      postTitle: postTitle ?? null,
    })

    return (
      <div className={shell}>
        <figure className={MEDIA_FIGURE_SHELL_CLASS} {...seoFigureAttributes}>
          <div className="mx-auto max-w-full">
            <Media htmlElement={null} resource={media} videoClassName="max-w-full rounded-lg" />
          </div>
          <figcaption className={hasCaption ? figcaptionRichClasses : figcaptionPlainClasses}>
            {hasCaption ? richCaptionNode : <span>{videoFigcaption}</span>}
          </figcaption>
        </figure>
      </div>
    )
  }

  // ── Imagem estática
  if (staticImage && typeof staticImage === 'object' && 'src' in staticImage) {
    const si = staticImage as StaticImageData
    const src = typeof si.src === 'string' ? si.src : String(si.src)
    const alt =
      postTitle?.trim() ||
      nearestHeading?.trim() ||
      staticAltFallback ||
      'Ilustração editorial de apoio ao conteúdo do artigo'
    const figText = buildContentFigcaptionText({
      alt,
      captionPlainFromCms: captionPlain,
      nearestHeading: nearestHeading ?? null,
      postTitle: postTitle ?? null,
    })
    // figText pode ser '' (caso "Imagem de apoio ao artigo" removido) → sem <figcaption> (evita caixa vazia).
    const figBody = hasCaption ? richCaptionNode : figText ? <span>{figText}</span> : null

    return (
      <div className={cn(shell, 'flex max-w-full min-w-0 flex-col items-center')}>
        <MediaFigure
          alt={alt}
          figcaption={figBody}
          figcaptionClassName={hasCaption ? figcaptionRichClasses : figcaptionPlainClasses}
          height={si.height}
          imgClassName={imgClassName}
          priority={priority}
          sizes={inlineSizes}
          src={src}
          width={si.width}
          seoFigureAttributes={seoFigureAttributes}
        />
      </div>
    )
  }

  // ── Imagem Payload
  if (media && typeof media === 'object' && (media as MediaType).url) {
    const m = media as MediaType
    const src = getMediaUrl(m.url, m.updatedAt)
    const { width, height } = getMediaIntrinsicDimensions(m)

    const resolvedAlt = resolvePostContentImageAlt(m, {
      nearestHeading: nearestHeading ?? null,
      postTitle: postTitle ?? null,
    })

    const figText = buildContentFigcaptionText({
      alt: resolvedAlt,
      captionPlainFromCms: captionPlain,
      nearestHeading: nearestHeading ?? null,
      postTitle: postTitle ?? null,
    })

    // figText pode ser '' (caso "Imagem de apoio ao artigo" removido) → sem <figcaption> (evita caixa vazia).
    const figBody = hasCaption ? richCaptionNode : figText ? <span>{figText}</span> : null

    return (
      <div className={cn(shell, 'flex max-w-full min-w-0 flex-col items-center')}>
        <MediaFigure
          alt={resolvedAlt}
          figcaption={figBody}
          figcaptionClassName={hasCaption ? figcaptionRichClasses : figcaptionPlainClasses}
          height={height}
          imgClassName={imgClassName}
          priority={priority}
          sizes={inlineSizes}
          src={src}
          width={width}
          seoFigureAttributes={seoFigureAttributes}
        />
      </div>
    )
  }

  return null
}
